import { Component } from "react";
import { reportClientError } from "@/lib/errorReporter";

/**
 * Top-level ErrorBoundary — catches any render-time exception in the
 * app tree and shows a friendly "something went wrong" card with a
 * Reload button, instead of blanking to white or hanging on a spinner.
 *
 * Every caught error is forwarded to the backend error reporter so we
 * get visibility into production crashes without needing a Sentry key.
 * If the error boundary itself fails (getDerivedStateFromError throws),
 * React unmounts to a blank screen and the wired window.error handler
 * in errorReporter.js still logs it as a fallback.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Fire-and-forget report — never blocks the fallback UI. `info`
    // has componentStack showing exactly which React tree crashed,
    // which is usually more useful than the raw JS stack.
    reportClientError({
      kind: "react.render_error",
      message: error?.message || String(error),
      stack: error?.stack || "",
      component_stack: info?.componentStack || "",
    });
    // Also mirror to the console so a developer with DevTools open
    // sees the crash as they normally would.
    console.error("[ErrorBoundary]", error, info);
  }

  handleReload = () => {
    // Reset local state first so if the user clicks Reload and lands
    // on the same crashing tree we don't get stuck on the fallback.
    this.setState({ error: null });
    // A full reload clears any bad in-memory state (stale token,
    // orphaned context) and re-fetches the latest JS bundle.
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    // Minimal, dependency-free markup — a broken app can't rely on
    // its own component library being intact.
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          background: "#f8fafc",
          fontFamily: "Manrope, system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "2rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: "50%",
                background: "rgba(200,125,58,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#c87d3a", fontWeight: 700, fontSize: 20,
              }}
            >
              !
            </div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", margin: 0 }}>
              Something went wrong loading this page
            </h1>
          </div>
          <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
            We&apos;ve logged the error automatically and someone will look into it.
            You can usually recover by reloading — if the problem repeats,
            please tell your admin the exact page and what you clicked.
          </p>
          {this.state.error?.message ? (
            <details
              style={{
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                padding: "0.75rem 1rem",
                marginBottom: 20,
                fontSize: 12,
                color: "#334155",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                Technical detail
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                }}
              >
                {this.state.error.message}
              </pre>
            </details>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="error-boundary-reload"
              onClick={this.handleReload}
              style={{
                background: "#100f5d",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0.65rem 1.25rem",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
            <a
              data-testid="error-boundary-home"
              href="/"
              style={{
                background: "#fff",
                color: "#100f5d",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "0.65rem 1.25rem",
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
